import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Conditional className composer — Tailwind-class-aware. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a number with appropriate decimals based on magnitude. */
export function fmtPrice(n: number | null | undefined, dec = 2): string {
  if (n == null || !isFinite(n)) return '—';
  return n.toLocaleString('en-GB', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/** Format a percent change like "+1.24%". */
export function fmtPct(n: number | null | undefined, dec = 2): string {
  if (n == null || !isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(dec)}%`;
}

/** Compact "5s ago / 12m ago / 3h ago" style relative time. */
export function timeAgo(input: string | Date | number): string {
  const t = typeof input === 'string' ? new Date(input).getTime() : input instanceof Date ? input.getTime() : input;
  if (!isFinite(t)) return '';
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** Bias label → tailwind text colour class. */
export function biasColor(bias: string | null | undefined): string {
  const b = (bias || '').toLowerCase();
  if (b.includes('bull')) return 'text-bull';
  if (b.includes('bear')) return 'text-bear';
  return 'text-fg-muted';
}

/** Returns hh:mm UTC string for a Date or ISO string. */
export function utcTime(d: string | Date): string {
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC', hour12: false });
}

/** Tiny stable hash for keys. */
export function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
